/*----------------------------------
- MIXPANEL
----------------------------------*/

/**
 * @docs docs/features/website-seo-performance
 */

// The async-modules build, not the package default. The default entry bundles the session
// recorder (rrweb) into the app: about 330 KB minified, the largest share of the JavaScript
// every landing page parsed before it could hydrate. This build is the same core and
// fetches the recorder from Mixpanel's CDN only once recording starts, off the critical
// path. Session recording itself is unchanged (`record_sessions_percent` below).
import mixpanel from 'mixpanel-browser/dist/mixpanel-with-async-modules.cjs.js';
import type { JsonValue } from '@/common/types/json';
import { runWhenIdleAfterLoad } from '@/client/pages/landing/lib/idle';
import { MIXPANEL_PROJECT_TOKEN } from '@uniquedomains/components/contracts/analytics/mixpanel';

const LOCAL_MIXPANEL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]']);

let initialized = false;
let analyticsListenerInstalled = false;
let initScheduled = false;

/*
 * Events and the identity that arrive before the SDK is initialised. Init is deferred to an
 * idle slot after `load` (see `scheduleMixpanelInit`), and every landing page tracks its
 * page view in a mount effect, so those calls would otherwise either be lost or pull the
 * whole SDK plus the session recorder back onto the first paint. Each queued event keeps
 * its own `time`, the Mixpanel property that overrides the send time, so the flush does
 * not re-date them.
 */
type QueuedEvent = { event: string; properties: Record<string, JsonValue> };
let queuedEvents: QueuedEvent[] = [];
let queuedProfile: MixpanelUserProfile | null = null;

/**
 * The recorder bundle matching the installed core. The async-modules build fetches the
 * unminified recorder by default (about 170 KB gzipped); this is the minified twin of the
 * same file, so the two never disagree on a version. A contract test checks that the file
 * still exists in the installed package, so a Mixpanel upgrade cannot silently pair a new
 * core with an old recorder.
 */
const MIXPANEL_RECORDER_SRC = 'https://cdn.mxpnl.com/libs/mixpanel-recorder-BjSlYaNJ.min.js';

type MixpanelEventProperties = Record<string, JsonValue | undefined>;

type BrowserAnalyticsEventDetail = MixpanelEventProperties & {
    event: string;
};

export type MixpanelUserProfile = {
    email?: string | null;
    name?: string | null;
};

const normalizeString = (value: string | null | undefined): string | undefined => {
    const trimmed = value?.trim();
    return trimmed || undefined;
};

const isLocalMixpanelBrowser = () => {
    if (typeof window === 'undefined') return true;
    if (__DEV__) return true;
    return LOCAL_MIXPANEL_HOSTNAMES.has(window.location.hostname);
};

const compactMixpanelProperties = (properties?: MixpanelEventProperties): Record<string, JsonValue> => {
    const compacted: Record<string, JsonValue> = {};
    if (!properties) return compacted;

    Object.entries(properties).forEach(([key, value]) => {
        if (typeof value === 'undefined') return;
        compacted[key] = value;
    });

    return compacted;
};

const isBrowserAnalyticsEventDetail = (
    detail: BrowserAnalyticsEventDetail | null | undefined,
): detail is BrowserAnalyticsEventDetail => {
    if (!detail || typeof detail !== 'object') return false;
    return Boolean(normalizeString(detail.event));
};

const toMixpanelEventProperties = (detail: BrowserAnalyticsEventDetail): MixpanelEventProperties => {
    const properties: MixpanelEventProperties = {};

    Object.entries(detail).forEach(([key, value]) => {
        if (key === 'event') return;
        properties[key] = value;
    });

    return properties;
};

const handleBrowserAnalyticsEvent: EventListener = (event) => {
    if (typeof CustomEvent !== 'function' || !(event instanceof CustomEvent)) return;

    const customEvent: CustomEvent<BrowserAnalyticsEventDetail> = event;
    if (!isBrowserAnalyticsEventDetail(customEvent.detail)) return;

    trackMixpanelEvent(customEvent.detail.event, toMixpanelEventProperties(customEvent.detail));
};

const installBrowserAnalyticsListener = () => {
    if (analyticsListenerInstalled || typeof window === 'undefined') return;

    window.addEventListener('ud:analytics', handleBrowserAnalyticsEvent);
    analyticsListenerInstalled = true;
};

export const initMixpanelBrowser = () => {
    if (isLocalMixpanelBrowser()) return;
    if (initialized) {
        installBrowserAnalyticsListener();
        return;
    }

    mixpanel.init(MIXPANEL_PROJECT_TOKEN, {
        autocapture: true,
        record_sessions_percent: 100,
        recorder_src: MIXPANEL_RECORDER_SRC,
    });

    initialized = true;
    installBrowserAnalyticsListener();

    const profile = queuedProfile;
    const events = queuedEvents;
    queuedProfile = null;
    queuedEvents = [];
    if (profile) identifyMixpanelUser(profile);
    for (const queued of events) mixpanel.track(queued.event, queued.properties);
};

/**
 * Initialises the SDK once the page is idle after `load`. Measured on the production
 * Home: initialising during render put the SDK and its recorder on the main thread before
 * the first paint (about 370 ms of the LCP render delay on a phone). Calls made before the
 * slot are queued and flushed by `initMixpanelBrowser`.
 */
export const scheduleMixpanelInit = () => {
    if (initScheduled || initialized || isLocalMixpanelBrowser()) return;
    initScheduled = true;

    // Listen from now, not from the idle slot: `landing_view` and `page_view` are dispatched
    // on the bus during hydration, seconds before the SDK initialises, and the bus is their
    // only route since the Website stopped posting to `/ping` (ADR-0036). With the listener
    // up, `trackMixpanelEvent` queues them and `initMixpanelBrowser` flushes the queue.
    installBrowserAnalyticsListener();
    runWhenIdleAfterLoad(initMixpanelBrowser, 3000);
};

export const trackMixpanelEvent = (event: string, properties?: MixpanelEventProperties) => {
    const eventName = normalizeString(event);
    if (!eventName || isLocalMixpanelBrowser()) return;

    if (!initialized && initScheduled) {
        // Mixpanel reads `time` in seconds.
        queuedEvents.push({ event: eventName, properties: { time: Date.now() / 1000, ...compactMixpanelProperties(properties) } });
        return;
    }

    initMixpanelBrowser();
    mixpanel.track(eventName, compactMixpanelProperties(properties));
};

export const identifyMixpanelUser = (profile: MixpanelUserProfile | null | undefined) => {
    const email = normalizeString(profile?.email);
    if (!email || isLocalMixpanelBrowser()) return;

    if (!initialized && initScheduled) {
        queuedProfile = profile ?? null;
        return;
    }

    initMixpanelBrowser();
    mixpanel.identify(email);
    mixpanel.people.set(
        compactMixpanelProperties({
            $email: email,
            $name: normalizeString(profile?.name),
        }),
    );
};
